import dayjs, { Dayjs } from 'dayjs'
import utc from 'dayjs/plugin/utc'

dayjs.extend(utc)

/**
 * UTC date helpers — the date range picker stores dates as UTC epoch seconds
 * and reasons about them entirely in UTC, matching v1 and the database. The
 * bug these fix is that dayjs defaults to LOCAL mode when it wraps a tz-less
 * value (a JS Date or an antd picker value), silently shifting the
 * displayed/stored day by the viewer's offset. These helpers pin everything
 * to UTC; they do not convert between timezones.
 */

/**
 * Pin a stored Date to UTC mode. A JS Date is just an epoch (no timezone); this
 * selects UTC for formatting/comparison without moving the instant. Use for
 * init-from-prop and display.
 */
export const toUtcDayjs = (date: Date | null | undefined): Dayjs =>
  dayjs(date ?? undefined).utc()

/**
 * Reinterpret a local-mode dayjs's calendar fields as UTC (keepLocalTime).
 * Needed at exactly ONE boundary: antd's <DatePicker> always emits a local-mode
 * dayjs we don't construct. Everywhere else we build UTC values directly
 * (dayjs.utc(...)), so this should not spread to new call sites.
 */
export const wallClockToUtc = (d: Dayjs): Dayjs => d.utc(true)

/** UTC midnight of the given day. */
export const utcStartOfDay = (d: Dayjs): Dayjs => d.startOf('day')

/**
 * UTC end-of-day, 23:59:59.000 — the last whole second, since stored bounds
 * are epoch seconds and the dialect's `between` is inclusive to the second.
 */
export const utcEndOfDay = (d: Dayjs): Dayjs =>
  d.hour(23).minute(59).second(59).millisecond(0)

/** Format a stored Date in UTC wall-clock. */
export const formatUtc = (date: Date, fmt = 'MMM D, YYYY'): string =>
  dayjs(date).utc().format(fmt)

/**
 * Parse a backend time-bucket category as UTC. Backend bucket values are
 * tz-less ISO datetimes (e.g. "2026-05-01T00:00:00"); `new Date(str)` parses
 * those as browser-LOCAL time, silently shifting a month/quarter/year bucket
 * for viewers ahead of UTC (a May 1 UTC boundary reads as Apr 30 local, so
 * the bucket mislabels as "Apr"). `dayjs.utc(str)` parses in UTC mode so the
 * resulting instant matches the backend boundary regardless of viewer TZ.
 */
export const parseBucketTimestamp = (timestamp: string | number | Date): Date =>
  dayjs.utc(timestamp).toDate()
